import React, { FC } from 'react';
import { useTheme } from 'styled-components';

interface InvoiceInfoProps {
  invoiceData: {
    paid: number;
    unpaid: number;
    canceled: number;
    refunded: number;
  };
}

const InvoiceInfo: FC<InvoiceInfoProps> = ({ invoiceData }) => {
  const invoiceStats = [
    {
      label: 'Paid',
      value: invoiceData.paid,
      color: '#4CAF50',
      icon: '✓'
    },
    {
      label: 'Unpaid',
      value: invoiceData.unpaid,
      color: '#FF9800',
      icon: '⏳'
    },
    {
      label: 'Canceled',
      value: invoiceData.canceled,
      color: '#F44336',
      icon: '✕'
    },
    {
      label: 'Refunded',
      value: invoiceData.refunded,
      color: '#9C27B0',
      icon: '↩'
    }
  ];

  const total = invoiceData.paid + invoiceData.unpaid + invoiceData.canceled + invoiceData.refunded;
  const theme: any = useTheme();
  const palette = theme?.palette ?? {};
  const colors = {
    cardBg: palette[10] ?? '#ffffff',
    cardBorder: palette[300] ?? '#e2e8f0',
    cardShadow: theme?.isDark ? '0 1px 3px rgba(15, 23, 42, 0.35)' : '0 1px 3px rgba(15, 23, 42, 0.08)',
    heading: palette[850] ?? '#0f172a',
    mutedText: palette[700] ?? '#64748b',
    highlightText: palette[850] ?? '#0f172a',
    surfaceMuted: palette[20] ?? '#f8fafc'
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: colors.cardBg,
    borderRadius: '8px',
    padding: '20px',
    boxShadow: colors.cardShadow,
    border: `1px solid ${colors.cardBorder}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
  };

  return (
    <div style={cardStyle}>
      <h2 style={{ 
        color: colors.heading,
        fontSize: '16px',
        fontWeight: '600',
        textAlign: 'center'
      }}>
        Invoice Statistics
      </h2>
      
      <div style={{
        backgroundColor: colors.surfaceMuted,
        border: `1px solid ${colors.cardBorder}`,
        borderRadius: '8px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px'
      }}>
        <div style={{ fontSize: '14px', fontWeight: 500, color: colors.mutedText }}>
          Total Invoices
        </div>
        <div style={{ fontSize: '28px', fontWeight: 700, color: colors.highlightText }}>
          {total.toLocaleString()}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {invoiceStats.map((stat) => {
          const percent = total > 0 ? Math.round((stat.value / total) * 100) : 0;
          return (
            <div
              key={stat.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '12px 14px',
                borderRadius: '8px',
                border: `1px solid ${colors.cardBorder}`,
                backgroundColor: colors.surfaceMuted
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '20px', color: stat.color }}>{stat.icon}</span>
                <span style={{ fontSize: '14px', fontWeight: 600, color: colors.highlightText, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {stat.label}
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '16px', fontWeight: 700, color: colors.highlightText }}>
                  {stat.value.toLocaleString()}
                </span>
                <span style={{
                  padding: '4px 10px',
                  borderRadius: '9999px',
                  backgroundColor: `${stat.color}20`,
                  color: stat.color,
                  fontSize: '12px',
                  fontWeight: 600,
                  minWidth: '48px',
                  textAlign: 'center'
                }}>
                  {percent}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InvoiceInfo;
